// Pages Management Component
// Allows users to create and manage custom portal pages
import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Eye, EyeOff, GripVertical, Save, X, FileText, Home as HomeIcon } from 'lucide-react';
import pagesService from '../../services/pagesService';
import PageBuilder from './PageBuilder';

const PagesManagement = () => {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPage, setSelectedPage] = useState(null);
  const [showPageBuilder, setShowPageBuilder] = useState(false);
  const [showNewPageModal, setShowNewPageModal] = useState(false);
  const [newPageData, setNewPageData] = useState({
    title: '',
    slug: '',
    isPublished: false,
    isHomePage: false,
    showInNavigation: true,
    navigationOrder: 0,
    metaDescription: '',
    metaKeywords: ''
  });
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPages();
  }, []);

  const fetchPages = async () => {
    try {
      setLoading(true);
      setError(null);
      const pagesData = await pagesService.getPages();
      setPages(Array.isArray(pagesData) ? pagesData : []);
    } catch (error) {
      console.error('Error fetching pages:', error);
      setError('Failed to load pages');
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePage = async (e) => {
    e.preventDefault();
    try {
      setError(null);

      // Auto-generate slug from title if not provided
      let slug = newPageData.slug;
      if (!slug && newPageData.title) {
        slug = newPageData.title
          .toLowerCase()
          .replace(/[^a-z0-9\s-]/g, '')
          .replace(/\s+/g, '-')
          .replace(/-+/g, '-')
          .trim();
      }

      const response = await pagesService.createPage({
        ...newPageData,
        slug
      });

      // Refresh pages list
      await fetchPages();

      // Close modal and reset form
      setShowNewPageModal(false);
      setNewPageData({
        title: '',
        slug: '',
        isPublished: false,
        isHomePage: false,
        showInNavigation: true,
        navigationOrder: 0,
        metaDescription: '',
        metaKeywords: ''
      });

      // Open the new page in the builder
      if (response.page) {
        setSelectedPage(response.page);
        setShowPageBuilder(true);
      }
    } catch (error) {
      console.error('Error creating page:', error);
      setError(error.message || 'Failed to create page');
    }
  };

  const handleDeletePage = async (pageId) => {
    if (!confirm('Are you sure you want to delete this page? All content blocks will be removed.')) {
      return;
    }

    try {
      setError(null);
      await pagesService.deletePage(pageId);
      await fetchPages();
    } catch (error) {
      console.error('Error deleting page:', error);
      setError('Failed to delete page');
    }
  };

  const handleTogglePublished = async (pageId, currentStatus) => {
    try {
      setError(null);
      await pagesService.updatePage(pageId, {
        isPublished: !currentStatus
      });
      await fetchPages();
    } catch (error) {
      console.error('Error updating page:', error);
      setError('Failed to update page status');
    }
  };

  const handleEditPage = async (page) => {
    try {
      setError(null);
      const pageData = await pagesService.getPage(page.id);
      setSelectedPage(pageData);
      setShowPageBuilder(true);
    } catch (error) {
      console.error('Error loading page:', error);
      setError('Failed to load page');
    }
  };

  const generateSlugPreview = (title) => {
    if (!title) return '';
    return title
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  if (showPageBuilder && selectedPage) {
    return (
      <PageBuilder
        page={selectedPage}
        onClose={() => {
          setShowPageBuilder(false);
          setSelectedPage(null);
          fetchPages();
        }}
      />
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-[#3C3C3C] dark:text-gray-100">Pages Management</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Create and manage custom pages for your portal
          </p>
        </div>
        <button
          onClick={() => setShowNewPageModal(true)}
          className="px-4 py-2 bg-[#7A99D8] text-white rounded-lg hover:bg-[#6A89C8] flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          New Page
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}

      {/* Pages List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="text-gray-500 dark:text-gray-400">Loading pages...</div>
        </div>
      ) : pages.length === 0 ? (
        <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-[#3C3C3C] dark:text-gray-100 mb-2">
            No pages yet
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
            Create your first custom page to get started
          </p>
          <button
            onClick={() => setShowNewPageModal(true)}
            className="px-4 py-2 bg-[#7A99D8] text-white rounded-lg hover:bg-[#6A89C8]"
          >
            Create First Page
          </button>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {pages.map((page) => (
              <div
                key={page.id}
                className="p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 flex-1">
                    <GripVertical className="w-5 h-5 text-gray-400 cursor-move" />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="text-base font-semibold text-[#3C3C3C] dark:text-gray-100">
                          {page.title}
                        </h3>
                        {page.is_home_page && (
                          <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 rounded">
                            <HomeIcon className="w-3 h-3 inline mr-1" />
                            Home
                          </span>
                        )}
                        {page.is_published ? (
                          <span className="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 rounded">
                            Published
                          </span>
                        ) : (
                          <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 rounded">
                            Draft
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        /{page.slug}
                      </p>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleTogglePublished(page.id, page.is_published)}
                      className="p-2 text-gray-400 hover:text-[#7A99D8] dark:hover:text-[#7A99D8] rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                      title={page.is_published ? 'Unpublish' : 'Publish'}
                    >
                      {page.is_published ? (
                        <EyeOff className="w-4 h-4" />
                      ) : (
                        <Eye className="w-4 h-4" />
                      )}
                    </button>
                    <button
                      onClick={() => handleEditPage(page)}
                      className="p-2 text-gray-400 hover:text-[#7A99D8] dark:hover:text-[#7A99D8] rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                      title="Edit Page"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeletePage(page.id)}
                      className="p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-500 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                      title="Delete Page"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* New Page Modal */}
      {showNewPageModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-lg w-full">
            <form onSubmit={handleCreatePage}>
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-[#3C3C3C] dark:text-gray-100">
                  Create New Page
                </h3>
              </div>

              <div className="p-6 space-y-4">
                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-[#3C3C3C] dark:text-gray-100 mb-2">
                    Page Title *
                  </label>
                  <input
                    type="text"
                    required
                    value={newPageData.title}
                    onChange={(e) => setNewPageData({ ...newPageData, title: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-[#3C3C3C] dark:text-gray-100"
                    placeholder="About Us"
                  />
                </div>

                {/* Slug */}
                <div>
                  <label className="block text-sm font-medium text-[#3C3C3C] dark:text-gray-100 mb-2">
                    URL Slug
                  </label>
                  <input
                    type="text"
                    value={newPageData.slug}
                    onChange={(e) => setNewPageData({ ...newPageData, slug: e.target.value.toLowerCase() })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-[#3C3C3C] dark:text-gray-100"
                    placeholder={generateSlugPreview(newPageData.title) || 'about-us'}
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Leave blank to auto-generate from title
                  </p>
                </div>

                {/* Meta Description */}
                <div>
                  <label className="block text-sm font-medium text-[#3C3C3C] dark:text-gray-100 mb-2">
                    Meta Description (SEO)
                  </label>
                  <textarea
                    value={newPageData.metaDescription}
                    onChange={(e) => setNewPageData({ ...newPageData, metaDescription: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-[#3C3C3C] dark:text-gray-100"
                    rows="2"
                    placeholder="Brief description of this page for search engines"
                  />
                </div>

                {/* Options */}
                <div className="space-y-2">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={newPageData.showInNavigation}
                      onChange={(e) => setNewPageData({ ...newPageData, showInNavigation: e.target.checked })}
                      className="rounded border-gray-300 dark:border-gray-600"
                    />
                    <span className="text-sm text-[#3C3C3C] dark:text-gray-100">
                      Show in navigation menu
                    </span>
                  </label>

                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={newPageData.isHomePage}
                      onChange={(e) => setNewPageData({ ...newPageData, isHomePage: e.target.checked })}
                      className="rounded border-gray-300 dark:border-gray-600"
                    />
                    <span className="text-sm text-[#3C3C3C] dark:text-gray-100">
                      Set as home page
                    </span>
                  </label>
                </div>
              </div>

              <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowNewPageModal(false);
                    setNewPageData({
                      title: '',
                      slug: '',
                      isPublished: false,
                      isHomePage: false,
                      showInNavigation: true,
                      navigationOrder: 0,
                      metaDescription: '',
                      metaKeywords: ''
                    });
                  }}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-[#3C3C3C] dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-[#7A99D8] text-white rounded-lg hover:bg-[#6A89C8]"
                >
                  Create Page
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PagesManagement;
